import { useEffect, useLayoutEffect, useRef } from "react";

interface Props {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  multi?: boolean;
  className?: string;
}

export function EditableField({ value, onChange, placeholder, multi, className }: Props) {
  const base = `editable px-1 py-0.5 ${className ?? ""}`;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-grow the textarea so all content is visible without scrolling internally.
  // useLayoutEffect + the resize-on-mount-and-on-value-change covers initial render
  // (so seeded multi-line content shows full height) and live edits.
  useLayoutEffect(() => {
    if (!multi || !textareaRef.current) return;
    const el = textareaRef.current;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [value, multi]);

  // Re-measure when fonts finish loading — the initial scrollHeight can be off
  // by a few pixels before custom fonts are applied.
  useEffect(() => {
    if (!multi || !textareaRef.current) return;
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!textareaRef.current) return;
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      });
    }
  }, [multi]);

  if (multi) {
    return (
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`${base} min-h-[28px] overflow-hidden`}
        rows={1}
      />
    );
  }
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={base}
    />
  );
}
